import { BarsArrowDownIcon, BarsArrowUpIcon, ChevronLeftIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { InputType } from 'config'
import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Button, Checkbox } from 'stories/components'
import { InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

const additionalDocs = [
  { id: 0, name: 'Unsigned Loan Application' },
  { id: 0, name: 'HUD1' },
  { id: 0, name: 'HUD1A' },
  { id: 0, name: 'Initial Escrow Disclosure' },
]

export const DrawDocuments = ({
  documents: _documents,
  onSubmit,
  onBack,
  onChange,
  showHistory,
  inputs: _inputs,
}: {
  documents: Array<any>
  onSubmit: Function
  onBack: Function
  onChange: Function
  showHistory: Function
  inputs: Record<string, InputType>
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [documents, setDocuments] = useState<Array<any>>([])
  const [inputs, setInputs] = useState<Record<string, InputType>>(_inputs)
  const [nameSort, setNameSort] = useState<1 | -1 | 0>(0)

  const loan = useSelector((state: any) => state.loan)

  const downloadOptionInput = inputs.downloadOption

  useEffect(() => {
    setIsLoading(false)
    const filteredDocs = additionalDocs.filter(
      (item) => item.name !== (loan.transactionType === 'Purchase' ? 'HUD1A' : 'HUD1'),
    )
    const totalDocs = [..._documents, ...filteredDocs]
    totalDocs?.map((document: any) => {
      document.checked = true
    })
    setNameSort(0)
    setDocuments(totalDocs || [])
  }, [_documents])

  const onSortFile = () => {
    const next = nameSort === 1 ? -1 : 1
    setNameSort(next)
    const newData = cloneDeep(documents)
    newData.sort((a, b) => {
      const cmp = String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' })
      return cmp * next
    })
    setDocuments(newData)
  }

  const onUpdate = (item: any, key: string, value: any) => {
    const newData = cloneDeep(documents)
    const index = documents.findIndex((v) => (v.id && v.id == item.id) || (!v.id && v.name == item.name))
    if (index == -1) return
    newData[index] = {
      ...item,
      [key]: value,
    }

    setDocuments(newData)
  }

  const onCheckAll = (value: boolean) => {
    const newData = cloneDeep(documents)
    newData.map((item: any) => {
      item.checked = value
    })
    setDocuments(newData)
  }

  const _onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setInputs(newInputs)
    onChange(key, value)
  }

  const onDrawDocument = async () => {
    const key = 'downloadOption'
    let hasError

    const newInputs = cloneDeep(inputs)
    newInputs[key].error = InputValidate(newInputs[key])
    if (newInputs[key].error) hasError = true

    setInputs(newInputs)
    if (hasError) return

    setIsLoading(true)
    const ids = documents.filter((item) => item.checked && item.id).map((item) => item.id)
    const additionalDocs = documents.filter((item) => item.checked && !item.id).map((item) => item.name)
    await onSubmit(ids, additionalDocs)
    setIsLoading(false)
    onBack()
  }

  const isAllChecked = useMemo(() => {
    return !!documents.length && documents.filter((v) => v.checked).length == documents.length
  }, [documents])

  return (
    <div className="GenerateDocument-container relative">
      <LayoutLoading show={isLoading} />
      <div className="text-shade-blue flex items-center mb-2 font-bold">
        <ChevronLeftIcon className="w-4 h-4"></ChevronLeftIcon>
        <span className="hover:underline cursor-pointer" onClick={() => onBack()}>
          Back
        </span>
      </div>
      <h2 className="text-2xl font-bold flex items-center mb-3">Draw Documents</h2>
      <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400">
        <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
          <tr>
            <th scope="col" className="px-3 py-3 w-[20px]">
              <Checkbox
                id={`check-no-all`}
                fontClass="!font-semibold !text-gray-700 !text-xs"
                title="No"
                checked={isAllChecked}
                onChange={(v) => onCheckAll(v)}
              />
            </th>
            <th scope="col" className="px-3 py-3">
              <button
                type="button"
                className="flex items-center bg-transparent font-semibold uppercase tracking-wide"
                onClick={onSortFile}
              >
                File
                {nameSort === 1 ? (
                  <BarsArrowUpIcon className="w-3 h-3 ml-1" />
                ) : nameSort === -1 ? (
                  <BarsArrowDownIcon className="w-3 h-3 ml-1" />
                ) : (
                  <span className="w-3 h-3 ml-1" />
                )}
              </button>
            </th>
          </tr>
        </thead>
        <tbody>
          {documents.length ? (
            documents.map((item: any, index) => {
              return (
                <React.Fragment key={`file-${item.id || 'extra'}-${item.name}`}>
                  <tr className={`border-b dark:bg-gray-800 dark:border-gray-700`} key={index}>
                    <td scope="row" className="px-3 py-1 font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      <div>
                        <Checkbox
                          id={`check-${index + 1}`}
                          title={`${index + 1}`}
                          checked={item.checked}
                          onChange={(v) => onUpdate(item, 'checked', v)}
                        />
                      </div>
                    </td>

                    <td scope="row" className="px-3 py-1 font-medium text-gray-900 dark:text-white">
                      {item.name}
                    </td>
                  </tr>
                </React.Fragment>
              )
            })
          ) : (
            <tr>
              <td colSpan={6}>
                <p className="text-sm text-center">--- No data ---</p>
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="mt-6">
        <RenderInput
          input={downloadOptionInput}
          Key={'downloadOption'}
          onChange={_onChange}
          showHistory={showHistory}
        />
      </div>
      <div className="text-center mt-6">
        <Button onClick={onDrawDocument} loading={isLoading}>
          Draw documents
        </Button>
      </div>
    </div>
  )
}
